import { createContext, useContext, useEffect, useState } from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'
import { useSectionPermission } from 'hooks/usePermission'
import { useRouter } from 'next/router'
import { ExtraFieldsService } from 'services/ExtraFieldsService'

import { UploadFileType, UploadSettings } from 'utils/customFieldUpload'
import browserQueryToQueryString from 'utils/query-string'

import { theme } from 'styles/theme'

export interface FieldConfigurationState {
  title: string
  description?: string
  options?: { label: string; value: string }[]
  type?: 'file' | 'link' | 'text' | 'full_date' | 'month_year' | 'year_only'
  value?: string
  locations?: string[]
  accept_text?: string
  display_mode?: 'list' | 'dropdown'
  accepted_types?: UploadFileType[]
  multiple?: boolean
  max_files?: number
}
export interface CustomFieldConfigs {
  context_type:
    | 'customer'
    | 'internal_customer'
    | 'product_link'
    | 'contract_model'
    | 'register_link'
  created_at: string
  entity_type: string | null
  merchant_uuid: string
  position: number
  required: boolean
  updated_at: string
  entity_uuid?: string
  entity_title?: string
  uuid: string
}

export interface ExtraFieldState {
  document_file?: {
    document_fileable_type: string
    uuid: string
    doc_type: string
    status: string
    description: string
    created_at: string
    updated_at: string
    doc_file_url: string
    doc_file_name: string
    doc_file_size: number
  }
  custom_field_configs: CustomFieldConfigs[]
  field_type: 'text' | 'checkbox' | 'radio' | 'accept' | 'upload' | 'date'
  field_configuration: FieldConfigurationState
  uuid: string
  has_values?: boolean
  upload_settings?: UploadSettings
}

const parseLabel = Object.freeze({
  text: 'Texto',
  short_text: 'Texto curto',
  long_text: 'Texto longo',
  checkbox: 'Multi seleção',
  radio: 'Seleção única',
  accept: 'Termos e Condições',
  upload: 'Upload de arquivo',
  date: 'Data'
})

export interface ExtraFieldsContextProps {
  table: {
    rows: {
      title: JSX.Element
      type: string
      actions: JSX.Element
    }[]
    rowAction: (uuid: string) => void
    metadata: {
      page: number
      per: number
      total: number
    }
    loading: boolean
    isExistingData: boolean
    data: ExtraFieldState[]
    refetch: () => void
  }
  details: {
    open: boolean
    loading: boolean
    data: ExtraFieldState | null
    handleOpen: (uuid: string) => void
    handleClose: () => void
    fetchExtraField: (uuid: string) => void
    traduction: typeof parseLabel
  }
  edit: {
    open: boolean
    loading: boolean
    data: ExtraFieldState | null
    handleOpen: (uuid?: string, isDisabled?: boolean) => void
    handleClose: () => void
    fetchExtraField: (uuid: string) => void
  }
  dialog: {
    open: boolean
    title: string
    uuid: string
    handleOpenDialog: (
      title: string,
      uuid: string,
      isDisabled?: boolean
    ) => void
    handleCloseDialog: () => void
    isDisabled: boolean
  }
}

export const ExtraFieldsContext = createContext<ExtraFieldsContextProps>(
  {} as ExtraFieldsContextProps
)

export const ExtraFieldsProvider = ({
  children,
  shouldRun = true
}: {
  children: React.ReactNode
  shouldRun?: boolean
}) => {
  const [loadingTable, setLoadingTable] = useState(false)

  // O provider é montado em vários pontos (Settings, Link de Cadastro, editor
  // de texto). Sem `custom_fields:show` o endpoint responde 403 e o
  // interceptor do DataService vira toast de permissão (SC-10495) — o guard
  // fica aqui para cobrir todos eles de uma vez.
  const canViewExtraFields = useSectionPermission('custom_fields:show')

  const { query } = useRouter()
  const [dataTable, setDataTable] = useState<ExtraFieldState[]>([])
  const [metadata, setMetadata] = useState({
    page: 1,
    per: 10,
    total: 0
  })

  const [openModalDetails, setOpenModalDetails] = useState(false)
  const [openModalCreateAndEdit, setOpenModalCreateAndEdit] = useState(false)
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    uuid: '',
    isDisabled: false
  })
  const [loadingEditAndDetails, setLoadingEditAndDetails] = useState(false)
  const [dataEditAndDetails, setDataEditAndDetails] =
    useState<ExtraFieldState | null>(null)

  const handleOpenModalCreateAndEdit = (uuid?: string) => {
    if (uuid) {
      fetchExtraField(uuid)
    } else {
      setDataEditAndDetails(null)
    }
    setOpenModalCreateAndEdit(true)
  }

  const handleCloseModalCreateAndEdit = () => {
    setOpenModalCreateAndEdit(false)
    setDataEditAndDetails(null)
  }

  const handleOpenDialog = (
    title: string,
    uuid: string,
    isDisabled?: boolean
  ) => {
    setDialog({ open: true, title, uuid, isDisabled: !!isDisabled })
  }

  const handleCloseDialog = () => {
    setDialog({ open: false, title: '', uuid: '', isDisabled: false })
  }

  const handleOpenModalDetails = (uuid: string) => {
    fetchExtraField(uuid)
    setOpenModalDetails(true)
  }

  const handleCloseModalDetails = () => {
    setOpenModalDetails(false)
    setDataEditAndDetails(null)
  }

  const fetchExtraField = async (uuid: string) => {
    try {
      setDataEditAndDetails(null)
      setLoadingEditAndDetails(true)
      const response = await ExtraFieldsService.getByUUID(uuid)
      if (response.error) {
        setDataEditAndDetails(null)
      } else {
        setDataEditAndDetails(response.record)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingEditAndDetails(false)
    }
  }

  const mapperToRow = ({
    uuid,
    field_type,
    field_configuration: { title },
    custom_field_configs,
    has_values
  }: ExtraFieldState) => ({
    uuid,
    title: <Box py="8px">{title}</Box>,
    type: parseLabel[field_type],
    actions: (
      <Box
        justifyContent="flex-end"
        display="flex"
        alignItems="center"
        gap={2}
        mr={1}
      >
        <Tooltip title="Editar campo" arrow placement="top">
          <Icon
            icon="edit"
            colorSX="#231F20"
            onClick={(e) => {
              e.stopPropagation()
              handleOpenModalCreateAndEdit(uuid)
            }}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
        <Tooltip title="Excluir campo" arrow placement="top">
          <Icon
            icon="delete"
            colorSX={theme.palette.neutral[100]}
            onClick={(e) => {
              e.stopPropagation()
              handleOpenDialog(
                title,
                uuid,
                !!custom_field_configs.length || !!has_values
              )
            }}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
        <Tooltip title="Configurações do campo" arrow placement="top">
          <Icon
            icon="settings"
            colorSX={theme.palette.neutral[100]}
            onClick={(e) => {
              e.stopPropagation()
              handleOpenModalDetails(uuid)
            }}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
      </Box>
    )
  })

  const getAllExtraFields = async () => {
    try {
      setLoadingTable(true)

      const response = await ExtraFieldsService.getAll({
        filter_by: browserQueryToQueryString(
          query as { [key in string]: string }
        )
      })

      if (response.error) {
        setDataTable([])
        setMetadata({ page: 1, per: 25, total: 0 })
      } else {
        setDataTable(response?.records)
        setMetadata({
          page: response?.meta?.current_page - 1,
          per: query.per ? Number(query.per) : 25,
          total: response?.meta?.total_count
        })
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingTable(false)
    }
  }

  useEffect(() => {
    if (shouldRun && canViewExtraFields) {
      getAllExtraFields()
    }
  }, [query, canViewExtraFields])

  const value = {
    table: {
      rows: dataTable.length > 0 ? dataTable.map(mapperToRow) : [],
      rowAction: handleOpenModalDetails,
      metadata: {
        page: metadata.page,
        per: metadata.per,
        total: metadata.total
      },
      data: dataTable,
      loading: loadingTable,
      isExistingData: dataTable.length > 0,
      refetch: getAllExtraFields
    },
    edit: {
      open: openModalCreateAndEdit,
      loading: loadingEditAndDetails,
      data: dataEditAndDetails,
      handleOpen: handleOpenModalCreateAndEdit,
      handleClose: handleCloseModalCreateAndEdit,
      fetchExtraField
    },
    details: {
      open: openModalDetails,
      loading: loadingEditAndDetails,
      data: dataEditAndDetails,
      handleOpen: handleOpenModalDetails,
      handleClose: handleCloseModalDetails,
      fetchExtraField,
      traduction: parseLabel
    },
    dialog: {
      open: dialog.open,
      title: dialog.title,
      uuid: dialog.uuid,
      isDisabled: dialog.isDisabled,
      handleOpenDialog,
      handleCloseDialog
    }
  }

  return (
    <ExtraFieldsContext.Provider value={value}>
      {children}
    </ExtraFieldsContext.Provider>
  )
}

export const useExtraFieldsContext = (): ExtraFieldsContextProps => {
  const context = useContext(ExtraFieldsContext)

  if (!context) {
    throw new Error(
      'useExtraFieldsContext should be used within NotificationsProvider.'
    )
  }

  return context
}
